import type { UUID } from "@/Shared/types";
import { Center } from "@freightsimple/ui-core/PageLayout/Center";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useShipmentService } from "@/Services/ShipmentService";
import BookShipmentBookingInProgress from "./CustomComponents/BookShipmentBookingInProgress";

interface BookShipmentProcessingScreenProps {
  shipmentId: UUID;
}

export interface CompletionResult {
  completedByAnimation: boolean;
}

/** How long we'll wait for the carrier to confirm before giving up on it. */
const BOOKING_POLL_SECONDS = 90;

export function BookShipmentProcessingScreen(
  props: BookShipmentProcessingScreenProps,
) {
  const shipmentsService = useShipmentService();
  const { shipmentId } = props;
  const navigate = useNavigate();
  const [isComplete, setIsCompleted] = useState(false);
  const pollTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  // One budget for the whole wait, not one per poller. When the animation
  // finished first we used to restart the poll with a fresh five seconds —
  // but a booking routinely takes longer than that, so a shipment that was
  // seconds from confirming sent the customer to "nearly there" instead.
  const secondsRemainingRef = useRef(BOOKING_POLL_SECONDS);

  const stopPolling = () => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = undefined;
    }
  };

  async function pollForBooking(onComplete: (success: boolean) => void) {
    if (secondsRemainingRef.current <= 0) {
      onComplete(false);
      return;
    }
    secondsRemainingRef.current -= 1;

    try {
      const shipment = await shipmentsService.getShipment(shipmentId);

      if (shipment.shipment?.shipmentState === ShipmentState.BookingConfirmed) {
        onComplete(true);
        return;
      }

      if (shipment.shipment?.shipmentState === ShipmentState.BookingFailed) {
        onComplete(false);
        return;
      }

      if (shipment.shipment?.shipmentState !== ShipmentState.BookingRequested) {
        navigate(`/view-shipment?shipmentId=${shipmentId}`);
        return;
      }

      pollTimeoutRef.current = setTimeout(() => {
        pollForBooking(onComplete);
      }, 1000);
    } catch (e) {
      console.error(e);
      onComplete(false);
    }
  }

  const onBookingError = () =>
    navigate(`/book/nearlydone?shipmentId=${shipmentId}`);

  const navigateToSuccess = async () => {
    const wasThisTheFirstShipmentBooked =
      await shipmentsService.hasBookedOnlyOneShipment();

    const destination = wasThisTheFirstShipmentBooked
      ? `/booking-success?shipmentId=${shipmentId}`
      : `/view-shipment?shipmentId=${shipmentId}`;

    navigate(destination);
  };

  const handleProgressComplete = async (result: CompletionResult) => {
    try {
      if (result.completedByAnimation) {
        // Animation finished first — restart the poll on whatever is left of
        // the shared budget, so the wait doesn't get cut short.
        stopPolling();
        pollForBooking(async (success) => {
          if (success) {
            await navigateToSuccess();
          } else {
            onBookingError();
          }
        });

        return;
      }

      await navigateToSuccess();
    } catch (e) {
      console.error(e);
      onBookingError();
    }
  };

  useEffect(() => {
    pollForBooking((success) => {
      if (!success) {
        onBookingError();
      }
      setIsCompleted(success);
    });

    return () => {
      stopPolling();
    };
  }, []);

  return (
    // The booking animation owns the screen while we wait, so centre it in a
    // near-viewport-height region rather than letting it sit at the top.
    <Center minHeight="90vh">
      <BookShipmentBookingInProgress
        onComplete={handleProgressComplete}
        isComplete={isComplete}
      />
    </Center>
  );
}
